/**
 * AI diagnosis store — Track A.
 *
 * Backs the /diagnose page. Three operations against the api-shim:
 *   run(slug)        → POST /diagnose/{slug}         — fresh triage diagnosis
 *   loadHistory(slug)→ GET  /diagnose/{slug}/history — past diagnoses (rail)
 *   loadDetail(id)   → GET  /diagnoses/{id}          — one diagnosis, full
 *
 * Thin fetch+cache layer — the page is the orchestrator.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, ApiError } from "@/api/client";

export type Verdict = "support_resolvable" | "escalate";
export type Confidence = "high" | "medium" | "low";

/** The structured triage report — mirrors diagnosis.py's report_diagnosis tool. */
export interface DiagnosisReport {
  summary: string;
  probableCause: string;
  confidence: Confidence;
  verdict: Verdict;
  verdictReason: string;
  recommendedAction: string;
  affectedDevices: string[];
  evidence: string[];
}

/** One unhealthy device row inside the dossier. */
export interface DossierDevice {
  serial: string;
  name: string;
  model: string;
  deviceClass: string;
  status: string;
  lanIp: string | null;
}

/** The raw evidence bundle the model was given — kept for auditability. */
export interface DiagnosisDossier {
  site: { slug: string; code: string | null; org: string | null };
  deviceState: {
    deviceCount: number;
    statusTotals: Record<string, number>;
    byClass: Record<string, Record<string, number>>;
    healthyDeviceCount: number;
    unhealthyDevices: DossierDevice[];
    unhealthyTruncated: boolean;
  };
  recentCompliance: unknown[];
  recentAuditEvents: unknown[];
  recentWorkflowRuns: unknown[];
}

/** POST /diagnose/{slug} response — a fresh run. */
export interface DiagnosisRun {
  id: string;
  site: { slug: string; code: string | null; org: string | null };
  result: DiagnosisReport;
  dossier: DiagnosisDossier;
  model: string;
  elapsedMs: number;
  usage: { inputTokens: number; outputTokens: number };
}

/** GET /diagnose/{slug}/history entry — summary shape, no blobs. */
export interface DiagnosisHistoryItem {
  id: string;
  createdAt: string;
  createdBy: string;
  networkId: string;
  siteSlug: string;
  siteName: string | null;
  verdict: Verdict;
  confidence: Confidence;
  summary: string;
  modelName: string | null;
  durationMs: number | null;
}

/** GET /diagnoses/{id} — a history item plus the full result + dossier. */
export interface DiagnosisDetail extends DiagnosisHistoryItem {
  result: DiagnosisReport;
  dossier: DiagnosisDossier | null;
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const detail =
      err.data && typeof err.data === "object" && "detail" in err.data
        ? String((err.data as { detail: unknown }).detail)
        : null;
    return detail ?? `API error ${err.status}`;
  }
  return err instanceof Error ? err.message : "unknown error";
}

interface State {
  running: Record<string, boolean>;
  loadingHistory: Record<string, boolean>;
  loadingDetail: Record<string, boolean>;
  historyBySlug: Record<string, DiagnosisHistoryItem[]>;
  detailById: Record<string, DiagnosisDetail>;
  errorBySlug: Record<string, string>;

  run: (slug: string, vendor?: "meraki" | "aruba" | "cli") => Promise<DiagnosisRun | null>;
  loadHistory: (slug: string) => Promise<void>;
  loadDetail: (id: string) => Promise<DiagnosisDetail | null>;
}

export const useDiagnose = create<State>((set, get) => ({
  running: {},
  loadingHistory: {},
  loadingDetail: {},
  historyBySlug: {},
  detailById: {},
  errorBySlug: {},

  run: async (slug, vendor = "meraki") => {
    if (!slug || get().running[slug]) return null;
    set((s) => ({
      running: { ...s.running, [slug]: true },
      errorBySlug: { ...s.errorBySlug, [slug]: "" },
    }));
    try {
      const qs = vendor && vendor !== "meraki" ? `?vendor=${encodeURIComponent(vendor)}` : "";
      const run = await apiPost<DiagnosisRun>(`/diagnose/${encodeURIComponent(slug)}${qs}`);
      set((s) => ({ running: { ...s.running, [slug]: false } }));
      void get().loadHistory(slug);
      return run;
    } catch (err) {
      set((s) => ({
        running: { ...s.running, [slug]: false },
        errorBySlug: { ...s.errorBySlug, [slug]: errMessage(err) },
      }));
      return null;
    }
  },

  loadHistory: async (slug) => {
    if (!slug || get().loadingHistory[slug]) return;
    set((s) => ({ loadingHistory: { ...s.loadingHistory, [slug]: true } }));
    try {
      const rows = await apiGet<DiagnosisHistoryItem[]>(
        `/diagnose/${encodeURIComponent(slug)}/history`,
      );
      set((s) => ({
        historyBySlug: { ...s.historyBySlug, [slug]: rows },
        loadingHistory: { ...s.loadingHistory, [slug]: false },
      }));
    } catch {
      set((s) => ({ loadingHistory: { ...s.loadingHistory, [slug]: false } }));
    }
  },

  loadDetail: async (id) => {
    if (!id) return null;
    const cached = get().detailById[id];
    if (cached) return cached;
    if (get().loadingDetail[id]) return null;
    set((s) => ({ loadingDetail: { ...s.loadingDetail, [id]: true } }));
    try {
      const detail = await apiGet<DiagnosisDetail>(`/diagnoses/${encodeURIComponent(id)}`);
      set((s) => ({
        detailById: { ...s.detailById, [id]: detail },
        loadingDetail: { ...s.loadingDetail, [id]: false },
      }));
      return detail;
    } catch {
      set((s) => ({ loadingDetail: { ...s.loadingDetail, [id]: false } }));
      return null;
    }
  },
}));
